import React, {
  cloneElement,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
} from "react";
import { useImmer } from "use-immer";

import { IgTooltip } from "@intelligems/ig-theme";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useAdminHasFullAccess } from "@src/api/user-auths/use-admin-has-full-access";
import { useDialogs } from "@src/hooks/dialog";

export interface FeatureToggleAnyWithDisableProps {
  children: ReactElement<{
    disabled: boolean;
    onClick?: () => void;
    onChange?: () => void;
  }>;
  isEnabled: boolean;
  adminDisable?: boolean; // when a component should be disabled for admins too
  customComponentWhenDisabled?: ReactNode;
  customDisabledTooltipMessage?: ReactNode;
  debugId: string;
  disableTooltip?: boolean;
  customEnabledTooltipMessage?: ReactNode;
  modalPopupProps?: {
    title: string;
    message: string;
    yesButtonText?: string;
    noButtonText?: string;
    yesButtonAction?: () => void;
    noButtonAction?: () => void;
    showButtons?: boolean;
  };
}

export const ScopedComponentWithDisable: FC<
  FeatureToggleAnyWithDisableProps
> = ({
  children,
  customComponentWhenDisabled,
  debugId,
  customDisabledTooltipMessage,
  isEnabled,
  modalPopupProps,
  adminDisable,
  disableTooltip,
  customEnabledTooltipMessage,
}) => {
  const isAdmin = useIsAdmin();
  const { openDialog } = useDialogs();

  const adminHasFullAccess = useAdminHasFullAccess();

  const doAdminsHaveAccess = isAdmin && adminHasFullAccess && !adminDisable;

  const [previousEnabled, setPreviousEnabled] = useImmer<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (previousEnabled === undefined) {
      setPreviousEnabled(isEnabled);
    }
  }, [isEnabled]);

  useEffect(() => {
    if (
      previousEnabled &&
      !doAdminsHaveAccess &&
      !isEnabled &&
      modalPopupProps
    ) {
      setPreviousEnabled(isEnabled);
      openDialog("scoped", {
        title: modalPopupProps.title,
        message: modalPopupProps.message,
        yesButtonText: modalPopupProps.yesButtonText,
        yesButtonAction: modalPopupProps.yesButtonAction,
        noButtonText: modalPopupProps.noButtonText,
        noButtonAction: modalPopupProps.noButtonAction,
        showButtons: modalPopupProps.showButtons || true,
      });
    }
  }, [isEnabled]);

  if (customComponentWhenDisabled && !isEnabled && !doAdminsHaveAccess) {
    return (
      <IgTooltip
        title={
          customEnabledTooltipMessage
            ? customEnabledTooltipMessage
            : isEnabled || doAdminsHaveAccess || disableTooltip
              ? ""
              : customDisabledTooltipMessage ||
                "Please contact Intelligems to unlock this feature"
        }
      >
        <span>{customComponentWhenDisabled}</span>
      </IgTooltip>
    );
  }

  return (
    <IgTooltip
      title={
        customEnabledTooltipMessage && !doAdminsHaveAccess
          ? customEnabledTooltipMessage
          : isEnabled || doAdminsHaveAccess || disableTooltip
            ? ""
            : customDisabledTooltipMessage ||
              "Please contact Intelligems to unlock this feature"
      }
    >
      <span>
        {!isEnabled && !doAdminsHaveAccess
          ? cloneElement(children, {
              disabled: true,
            })
          : cloneElement(children, { disabled: false })}
      </span>
    </IgTooltip>
  );
};
